import { useCallback, useEffect, useRef, useState } from "react";
import {
  SAND_GRAM_ICON,
  SAND_PRMD_MASTER,
  SAND_CONTRACT_ADDRESS,
  fetchPrmdMeta,
  fetchRoundSeconds,
  fetchSandJettons,
  fetchSandRounds,
  fetchSandTokenValues,
  fetchWalletHoldings,
  fetchTokenPrices,
  type WalletHoldings,
} from "../ton/sand";

/** One Sand "table": a token that has (or can open) a round. */
export interface SandPool {
  key: string;
  isTon: boolean;
  symbol: string;
  decimals: number;
  image: string;
  master: string | null;
  wallet: string | null;
  featured: boolean;
  mine: boolean;
  live: boolean;
  endTime: number;
  betCount: number;
  totalPool: bigint;
  /** Prize-only part of totalPool with no winner-scan weight (see SandRound). */
  carry: bigint;
  /** Aggregate USD value of the token across the top players' wallets. */
  usd: number;
  /** USD value of the current pool at the live token price (0 = unpriced). */
  poolUsd: number;
}

const keyOf = (s: string) => s.replace(/[^a-zA-Z0-9_-]/g, "");

/**
 * Loads and periodically refreshes the Sand tables: every open round on the
 * contract plus every token in the connected wallet that could open one.
 * GRAM and PRMD are always present.
 */
export function useSand(address: string | null) {
  const [pools, setPools] = useState<SandPool[]>([]);
  const [holdings, setHoldings] = useState<WalletHoldings>({
    tonBalance: 0n,
    jettons: new Map(),
  });
  const [roundSeconds, setRoundSeconds] = useState(3600);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busy = useRef(false);
  // Last refined wallet holdings / prices — the fast repaint reuses them so
  // wallet-only tables don't vanish and reappear on every refresh cycle.
  const lastMine = useRef<WalletHoldings>({ tonBalance: 0n, jettons: new Map() });
  const lastPrices = useRef(new Map<string, number>());

  const refresh = useCallback(
    async (forceHoldings = false) => {
      if (busy.current) return;
      busy.current = true;
      try {
        const rounds = await fetchSandRounds();
        let sandJettons = await fetchSandJettons();
        // A brand-new table missing from the metadata cache — refresh it now.
        if (rounds.some((r) => !r.isTon && !sandJettons.has(r.wallet))) {
          sandJettons = await fetchSandJettons(true);
        }
        const values = await fetchSandTokenValues();

        // The tables themselves come from the fast backend snapshot; wallet
        // holdings and live prices go through the paced tonapi queue (seconds).
        // Build and show the list immediately, then refine it once the slow
        // data arrives — the page never sits on a spinner waiting for tonapi.
        const build = async (
          mine: WalletHoldings,
          prices: Map<string, number>,
        ): Promise<SandPool[]> => {
          const poolUsdOf = (raw: bigint, decimals: number, priceKey: string) =>
            (Number(raw) / 10 ** decimals) * (prices.get(priceKey) || 0);

          const byKey = new Map<string, SandPool>();
          const gramRound = rounds.find((r) => r.isTon) || null;
          byKey.set("gram", {
            key: "gram",
            isTon: true,
            symbol: "GRAM",
            decimals: 9,
            image: SAND_GRAM_ICON,
            featured: true,
            mine: true,
            live: !!gramRound,
            endTime: gramRound?.endTime || 0,
            betCount: gramRound?.betCount || 0,
            totalPool: gramRound?.totalPool || 0n,
            carry: gramRound?.carry || 0n,
            wallet: SAND_CONTRACT_ADDRESS,
            master: null,
            usd: 0,
            poolUsd: poolUsdOf(gramRound?.totalPool || 0n, 9, "ton"),
          });

          for (const r of rounds.filter((x) => !x.isTon)) {
            const meta = sandJettons.get(r.wallet) || {
              master: null,
              symbol: "???",
              decimals: 9,
              image: "",
            };
            const key = keyOf(meta.master || r.wallet);
            byKey.set(key, {
              key,
              isTon: false,
              symbol: meta.symbol,
              decimals: meta.decimals,
              image: meta.image,
              master: meta.master,
              wallet: r.wallet,
              featured: meta.master === SAND_PRMD_MASTER,
              mine: meta.master ? mine.jettons.has(meta.master) : false,
              live: true,
              endTime: r.endTime,
              betCount: r.betCount,
              totalPool: r.totalPool,
              carry: r.carry,
              usd: (meta.master && values.get(meta.master)?.usd) || 0,
              poolUsd: meta.master
                ? poolUsdOf(r.totalPool, meta.decimals, meta.master)
                : 0,
            });
          }

          const prmdKey = keyOf(SAND_PRMD_MASTER);
          if (!byKey.has(prmdKey)) {
            const my = mine.jettons.get(SAND_PRMD_MASTER);
            const meta = await fetchPrmdMeta();
            byKey.set(prmdKey, {
              key: prmdKey,
              isTon: false,
              symbol: meta.symbol,
              decimals: my?.decimals ?? meta.decimals,
              image: my?.image || meta.image,
              master: SAND_PRMD_MASTER,
              wallet: null,
              featured: true,
              mine: !!my,
              live: false,
              endTime: 0,
              betCount: 0,
              totalPool: 0n,
              carry: 0n,
              usd: values.get(SAND_PRMD_MASTER)?.usd || 0,
              poolUsd: 0,
            });
          }

          for (const [master, my] of mine.jettons) {
            const key = keyOf(master);
            if (byKey.has(key)) continue;
            // Wallet-only tokens without a DEX price are worthless to a table
            // (nobody can price a bet) and are typically scam mints — skip them.
            if (my.priceUsd <= 0) continue;
            byKey.set(key, {
              key,
              isTon: false,
              symbol: my.symbol,
              decimals: my.decimals,
              image: my.image,
              master,
              wallet: null,
              featured: false,
              mine: true,
              live: false,
              endTime: 0,
              betCount: 0,
              totalPool: 0n,
              carry: 0n,
              usd: values.get(master)?.usd || 0,
              poolUsd: 0,
            });
          }

          return [...byKey.values()];
        };

        // Fast paint: backend data plus the previous cycle's holdings/prices.
        setPools(await build(lastMine.current, lastPrices.current));
        setError(null);
        setLoaded(true);

        // Refine with wallet holdings and live prices (paced tonapi queue).
        const liveMasters = rounds
          .filter((x) => !x.isTon)
          .map((r) => sandJettons.get(r.wallet)?.master)
          .filter((m): m is string => !!m);
        const [mine, prices] = await Promise.all([
          fetchWalletHoldings(address, forceHoldings),
          fetchTokenPrices(liveMasters),
        ]);
        lastMine.current = mine;
        lastPrices.current = prices;
        setPools(await build(mine, prices));
        setHoldings(mine);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        busy.current = false;
      }
    },
    [address],
  );

  useEffect(() => {
    void fetchRoundSeconds().then(setRoundSeconds);
  }, []);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 10_000);
    return () => clearInterval(id);
  }, [refresh]);

  return { pools, holdings, roundSeconds, loaded, error, refresh };
}
